import { useRef, useEffect, useCallback, useState } from 'react';
import type { FieldDescriptor, ViewerMode } from './signingTypes';
import { PDF_VIEWER } from './pdfViewerTokens';
import { usePlaceholderValues } from '../../state/placeholderValuesStore';
import type { SignerColor } from '../../lib/signerColors';
import { isTitleMarkerField } from '@corner/shared';

interface FieldMarkerProps {
  field: FieldDescriptor;
  isActive: boolean;
  /** Whether this field is selected for inspection in configure mode. */
  isInspected?: boolean;
  mode: ViewerMode;
  signaturePreview?: string | null;
  /** Document key for the placeholder values store. */
  documentId?: string;
  onClick?: () => void;
  /** Called in configure mode when clicking for inspection. */
  onInspect?: (field: FieldDescriptor) => void;
  /** Called in configure mode when field is dragged to a new position. */
  onFieldMove?: (fieldId: string, rect: { x: number; y: number; width: number; height: number }) => void;
  /** v1 placement-redesign: per-signer palette slot. When undefined, falls
   *  back to the legacy global color (slot 0). */
  signerColor?: SignerColor;
  /** v1 placement-redesign: user clicked the × on the marker. The parent
   *  shows the DeleteFieldConfirm popover and dispatches the actual delete. */
  onRequestDelete?: (fieldId: string) => void;
  /** v1 placement-redesign: fired after a drag if the field was previously
   *  'suggested', so the parent can flip its state to 'confirmed' (orange→green). */
  onConfirmField?: (fieldId: string) => void;
}

/** A placeholder is "fillable inline" when it's a text field that the AI/user
 *  should replace (bracketed, highlighted, or any explicit placeholder style).
 *  These get the live-overlay UX. */
function isFillablePlaceholder(field: FieldDescriptor): boolean {
  if (field.type !== 'text') return false;
  const style = field.placeholderStyle;
  if (!style) return false;
  // Exclude pure underline-style "blank line" text fields — those keep the
  // underscore visible (no redaction needed in the overlay).
  return style !== 'underline';
}

const TYPE_LABELS: Record<string, string> = {
  signature: 'Sign',
  initials: 'Initial',
  date: 'Date',
  text: 'Text',
};

function markerDisplayLabel(field: FieldDescriptor): string {
  const role = (field.role ?? '').toLowerCase();
  const label = (field.label ?? '').toLowerCase();
  if (field.type === 'signature') return 'Sign';
  if (field.type === 'initials') return 'Initial';
  if (field.type === 'date' || /\bdate\b/.test(label) || role.endsWith('_date')) return 'Date';
  if (role.endsWith('_email') || /\bemail\b/.test(label)) return 'Email';
  if (isTitleMarkerField(field)) return 'Title';
  if (role.endsWith('_name') || /\bname\b|printed\s*name|full\s*name/.test(label)) return 'Name';
  return TYPE_LABELS[field.type] ?? 'Field';
}

// Corner bracket — small L-shaped mark at a corner
function Bracket({ position, color }: { position: 'tl' | 'tr' | 'bl' | 'br'; color: string }) {
  const size = 6;
  const thickness = 2;
  const base: React.CSSProperties = { position: 'absolute', borderColor: color, borderStyle: 'solid', borderWidth: 0 };
  const styles: Record<string, React.CSSProperties> = {
    tl: { ...base, top: 0, left: 0, borderTopWidth: thickness, borderLeftWidth: thickness, width: size, height: size },
    tr: { ...base, top: 0, right: 0, borderTopWidth: thickness, borderRightWidth: thickness, width: size, height: size },
    bl: { ...base, bottom: 0, left: 0, borderBottomWidth: thickness, borderLeftWidth: thickness, width: size, height: size },
    br: { ...base, bottom: 0, right: 0, borderBottomWidth: thickness, borderRightWidth: thickness, width: size, height: size },
  };
  return <div style={styles[position]} />;
}

export default function FieldMarker({ field, isActive, isInspected, mode, signaturePreview, documentId, onClick, onInspect, onFieldMove, signerColor, onRequestDelete, onConfirmField }: FieldMarkerProps) {
  const { signed, isForCurrentSigner } = field;
  const showAsOther = mode === 'sign' && !isForCurrentSigner;
  const isEmbeddedSigned = signed && field.state !== 'confirmed';
  // Any field with onFieldMove is draggable (user can reposition AI-placed markers)
  const isDraggable = !!onFieldMove && !showAsOther && !isEmbeddedSigned;
  // v1 placement-redesign: hover state controls × button visibility
  const [hovered, setHovered] = useState(false);

  const fillable = isFillablePlaceholder(field);
  const { values, setValue } = usePlaceholderValues(documentId);
  const placeholderValue = fillable ? values[field.id]?.value : undefined;
  const hasPlaceholderValue = typeof placeholderValue === 'string' && placeholderValue.length > 0;
  const [editingPlaceholder, setEditingPlaceholder] = useState(false);
  const [draftValue, setDraftValue] = useState('');

  const openPlaceholderEditor = useCallback(() => {
    setDraftValue(placeholderValue ?? '');
    setEditingPlaceholder(true);
  }, [placeholderValue]);

  const commitPlaceholder = useCallback((value: string) => {
    setValue(field.id, value, 'user');
    setEditingPlaceholder(false);
  }, [field.id, setValue]);

  const markerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    startClientX: number;
    startClientY: number;
    startRect: { x: number; y: number; width: number; height: number };
    moved: boolean;
  } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isDraggable) return;
    e.preventDefault();
    e.stopPropagation();
    dragStateRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startRect: { ...field.rect },
      moved: false,
    };
    // Force re-render to show grabbing cursor
    markerRef.current?.style.setProperty('cursor', 'grabbing');
    markerRef.current?.style.setProperty('z-index', '100');
    markerRef.current?.style.setProperty('box-shadow', '0 4px 16px rgba(0,0,0,0.25)');
    markerRef.current?.style.setProperty('opacity', '0.9');
  }, [isDraggable, field.rect]);

  useEffect(() => {
    if (!isDraggable) return;

    const handleMouseMove = (e: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds || !markerRef.current) return;

      const container = markerRef.current.parentElement;
      if (!container) return;

      const deltaX = e.clientX - ds.startClientX;
      const deltaY = e.clientY - ds.startClientY;

      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        ds.moved = true;
      }
      if (!ds.moved) return;

      const containerRect = container.getBoundingClientRect();
      const deltaXPct = (deltaX / containerRect.width) * 100;
      const deltaYPct = (deltaY / containerRect.height) * 100;

      const newX = Math.max(0, Math.min(100 - ds.startRect.width, ds.startRect.x + deltaXPct));
      const newY = Math.max(0, Math.min(100 - ds.startRect.height, ds.startRect.y + deltaYPct));

      onFieldMove!(field.id, {
        x: Math.round(newX * 100) / 100,
        y: Math.round(newY * 100) / 100,
        width: ds.startRect.width,
        height: ds.startRect.height,
      });
    };

    const handleMouseUp = () => {
      const ds = dragStateRef.current;
      if (!ds) return;

      // Reset visual overrides
      markerRef.current?.style.removeProperty('cursor');
      markerRef.current?.style.removeProperty('z-index');
      markerRef.current?.style.removeProperty('box-shadow');
      markerRef.current?.style.removeProperty('opacity');

      if (!ds.moved) {
        // Didn't drag — self title markers open the title editor; others inspect.
        const editableTitle =
          mode === 'configure' && isTitleMarkerField(field) && field.isRecipientParty !== true;
        if (editableTitle && onClick) {
          onClick();
        } else if (onInspect) {
          onInspect(field);
        }
      } else if (onConfirmField && (field.state === 'suggested' || field.state === undefined)) {
        // Drag = vouching for the position. Flip suggested → confirmed
        // so the marker shows vetted styling. Already-confirmed fields stay confirmed.
        onConfirmField(field.id);
      }

      dragStateRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggable, field, onFieldMove, onInspect, onConfirmField, onClick, mode]);

  const handleClick = () => {
    // In configure mode, click is handled by mouseup (drag vs click disambiguation)
    if (isDraggable) return;
    if (mode === 'configure' && onInspect) {
      if (onClick && isTitleMarkerField(field) && field.isRecipientParty !== true) onClick();
      else onInspect(field);
      return;
    }
    // Fillable placeholders open an inline editor; signature/initials/date keep
    // their existing onClick (sign flow).
    if (fillable && !showAsOther) {
      openPlaceholderEditor();
      return;
    }
    if (!signed && !showAsOther && onClick) onClick();
  };

  const canClick = isDraggable
    || (mode === 'configure' ? !!onInspect
      : ((fillable && !showAsOther) || (!signed && !showAsOther && !!onClick)));

  // v1 placement-redesign: when a signerColor is provided we honor the
  // suggested vs confirmed split per state. Otherwise fall back to the
  // legacy global palette (kept for sign-mode and any caller that hasn't
  // migrated yet — unchanged behavior).
  const useSignerPalette = !!signerColor && !showAsOther && !signed;
  const isTitleMarker = isTitleMarkerField(field);
  const titleSelfConfigure = mode === 'configure' && isTitleMarker && field.isRecipientParty !== true;
  const titleFilled = Boolean(field.source.signatureField?.printedText?.trim());
  const isRecipientPartyPlacement = mode === 'configure' && field.isRecipientParty === true && !signed;
  const isConfirmedState = titleSelfConfigure
    ? titleFilled
    : field.state === 'confirmed';

  let paletteOutline: string | undefined;
  let paletteBg: string | undefined;
  if (useSignerPalette && signerColor) {
    if (isConfirmedState) {
      // Self (palette slot 0 / owner-green) stays green when confirmed; every
      // other slot keeps that signer's hue — SIGNER_PALETTE.confirmed* wrongly
      // uses green for all slots, which made recipient chips look like self.
      const partyHueConfirmed =
        isRecipientPartyPlacement || signerColor.id !== 'owner-green';
      if (partyHueConfirmed) {
        paletteOutline = signerColor.otherPartyVettedOutline;
        paletteBg = `${signerColor.otherPartyVettedBg}CC`;
      } else {
        paletteOutline = signerColor.confirmedOutline;
        paletteBg = `${signerColor.confirmedBg}CC`;
      }
    } else {
      paletteOutline = signerColor.outline;
      paletteBg = `${signerColor.bg}99`;
    }
  }

  /** Owner overlay for counterparty rects: opaque green masked embedded PDF ink; let PDF pixels show through. */
  const transparentRecipientSignedBg = mode === 'sign' && showAsOther && signed;

  const color = signed
    ? PDF_VIEWER.fieldSigned
    : showAsOther
      ? PDF_VIEWER.fieldBorderOther
      : paletteOutline != null
        ? paletteOutline
        : isActive ? PDF_VIEWER.fieldActive : PDF_VIEWER.fieldUnsigned;

  const bgColor = transparentRecipientSignedBg
    ? 'transparent'
    : signed
      ? `${PDF_VIEWER.fieldBgSigned}44`
      : showAsOther
        ? `${PDF_VIEWER.fieldBgOther}88`
        : paletteBg != null
          ? paletteBg
          : isActive ? `${PDF_VIEWER.fieldBgActive}CC` : `${PDF_VIEWER.fieldBgUnsigned}66`;

  const tabLabel = signed
    ? 'Signed'
    : markerDisplayLabel(field);

  // v1 placement-redesign: × button visible while hovered or inspected,
  // and only when the marker has a delete callback (not in sign mode).
  const showDeleteButton = !!onRequestDelete && !showAsOther && (hovered || isInspected);

  return (
    <div
      ref={markerRef}
      onClick={canClick ? handleClick : undefined}
      onMouseDown={isDraggable ? handleMouseDown : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'absolute',
        left: `${field.rect.x}%`,
        top: `${field.rect.y}%`,
        width: `${field.rect.width}%`,
        height: `${field.rect.height}%`,
        cursor: isDraggable ? 'grab' : canClick ? 'pointer' : 'default',
        pointerEvents: 'auto',
        boxSizing: 'border-box',
        opacity: showAsOther ? 0.4 : 1,
        transition: 'opacity 0.15s',
        boxShadow: isInspected ? `0 0 0 2px ${color}, 0 0 8px ${color}44` : 'none',
      }}
      title={field.label}
    >
      {/* Background fill (suppressed when a placeholder value is rendered —
          the opaque white cover replaces it so the value reads cleanly). */}
      {!hasPlaceholderValue && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 2,
          backgroundColor: bgColor,
          transition: 'background-color 0.15s',
        }} />
      )}

      {/* Live placeholder overlay: opaque white rectangle covering the
          original ink + the typed value rendered on top. Until the user saves,
          this is purely cosmetic — the underlying PDF still says "[BUYER NAME]"
          but the user sees their value at the same coordinates. */}
      {fillable && hasPlaceholderValue && (
        <>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundColor: '#ffffff',
            borderRadius: 1,
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center',
            paddingLeft: 2, paddingRight: 2,
            fontSize: 11, fontWeight: 500,
            color: '#1a1a1a',
            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
            lineHeight: 1.1,
          }}>
            {placeholderValue}
          </div>
        </>
      )}

      {/* Corner brackets — only render when the placeholder is unfilled, so
          a "filled" placeholder visually disappears into the document flow.
          The top-right bracket is replaced by the × delete button while the
          marker is hovered/inspected (v1 placement-redesign). */}
      {!hasPlaceholderValue && (
        <>
          <Bracket position="tl" color={color} />
          {showDeleteButton ? (
            <button
              type="button"
              aria-label={`Delete ${field.label}`}
              onClick={(e) => {
                e.stopPropagation();
                onRequestDelete!(field.id);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: -8, right: -8,
                width: 18, height: 18, borderRadius: 9,
                border: `1px solid ${color}`,
                backgroundColor: '#fff',
                color,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0,
                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                fontSize: 11, lineHeight: 1, fontWeight: 700,
                zIndex: 10,
              }}
            >
              ×
            </button>
          ) : (
            <Bracket position="tr" color={color} />
          )}
          <Bracket position="bl" color={color} />
          <Bracket position="br" color={color} />
        </>
      )}

      {/* Inline placeholder editor popover — opens on click for fillable
          placeholders. Enter commits, Escape cancels. */}
      {editingPlaceholder && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '100%', left: 0,
            marginTop: 4,
            zIndex: 200,
            backgroundColor: '#fff',
            border: `1px solid ${PDF_VIEWER.fieldActive}`,
            borderRadius: 4,
            padding: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex', alignItems: 'center', gap: 4,
            minWidth: 180,
          }}
        >
          <input
            autoFocus
            type="text"
            value={draftValue}
            placeholder={field.placeholderRaw ?? field.label}
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitPlaceholder(draftValue);
              else if (e.key === 'Escape') setEditingPlaceholder(false);
              e.stopPropagation();
            }}
            style={{
              flex: 1, minWidth: 0,
              padding: '4px 6px',
              border: '1px solid #ddd', borderRadius: 3,
              fontSize: 12,
              outline: 'none',
            }}
          />
          <button
            onClick={() => commitPlaceholder(draftValue)}
            style={{
              padding: '4px 10px', borderRadius: 3,
              border: 'none', cursor: 'pointer',
              backgroundColor: PDF_VIEWER.fieldActive, color: '#fff',
              fontSize: 11, fontWeight: 600,
            }}
          >
            Set
          </button>
        </div>
      )}

      {/* Tab pill — anchored above the field */}
      {!showAsOther && (
        <div style={{
          position: 'absolute',
          top: -18, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 3,
          padding: '1px 8px', borderRadius: '6px 6px 0 0',
          backgroundColor: color, color: '#fff',
          fontSize: 9, fontWeight: 600, lineHeight: '16px',
          whiteSpace: 'nowrap',
          boxShadow: '0 -1px 4px rgba(0,0,0,0.1)',
        }}>
          {signed && (
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path d="M4 8l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {tabLabel}
        </div>
      )}

      {/* Inner content.
          CORNER_V2: For ink types (signature/initials/date) the bbox bottom sits
          on the underscore line baseline (set by snapFieldsToRegions on the backend),
          so signed previews are bottom-anchored — the ink visually rests on the line
          instead of being centered through it. UI hints (callouts, plain labels)
          remain centered.

          Note: once a date is signed, the actual date string is rendered INTO the
          PDF by embedSignatureImages (esign.ts). The overlay must NOT render its
          own date on top, otherwise the user sees two dates stacked. We render a
          small green check in the corner instead so the marker still reads as
          "done" without duplicating content. The same logic applies to text
          fields (no overlay content). Signature/initials are unique because the
          rendered preview overlays the embedded image at the same coordinates
          and the same image, so they appear as one. */}
      {(() => {
        const isInkType = field.type === 'signature' || field.type === 'initials' || field.type === 'date';
        const showSignedInk =
          signed && (
            (field.type === 'signature' && !!signaturePreview) ||
            (field.type === 'initials' && !!signaturePreview)
          );
        const inkAlign: React.CSSProperties = {
          position: 'absolute', inset: 0,
          display: 'flex', justifyContent: 'center',
          alignItems: isInkType && showSignedInk ? 'flex-end' : 'center',
          paddingBottom: isInkType && showSignedInk ? 1 : 0,
          overflow: 'hidden',
        };

        const recipientStoredText = field.source.signatureField?.printedText?.trim();
        const suppressSignedStoredTextOverlay = field.type === 'text' && isTitleMarker;
        const showSignedStoredText =
          signed &&
          !!recipientStoredText &&
          (field.type === 'date' || (field.type === 'text' && !suppressSignedStoredTextOverlay));

        return (
          <div style={inkAlign}>
            {showSignedStoredText && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 3px',
                fontSize: field.type === 'date' ? 11 : 10,
                fontWeight: 800,
                color: '#050505',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                lineHeight: 1.08,
                letterSpacing: 0,
                textShadow: '0 0 0 #050505',
              }}>
                {recipientStoredText}
              </div>
            )}
            {/* Active unsigned + signature preview available — show preview centered */}
            {isActive && !signed && !showAsOther && signaturePreview && (
              <img
                src={signaturePreview}
                alt="Preview"
                style={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain', opacity: 0.5 }}
              />
            )}
            {isActive && !signed && !showAsOther && !signaturePreview && (
              <span style={{ fontSize: 9, fontWeight: 600, color, textAlign: 'center', lineHeight: 1.3 }}>
                {field.type === 'signature' && 'Click to sign'}
                {field.type === 'initials' && 'Add initials'}
                {field.type === 'date' && 'Select date'}
                {field.type === 'text' && (
                  titleSelfConfigure
                    ? 'Click to add title'
                    : isTitleMarker
                      ? "Recipient's title"
                      : markerDisplayLabel(field) === 'Email'
                        ? 'Signer email'
                        : 'Enter text'
                )}
              </span>
            )}

            {/* Inactive unsigned: subtle label, centered */}
            {!isActive && !signed && !showAsOther && (
              <span style={{
                fontSize: 8, fontWeight: 500, color,
                textAlign: 'center', lineHeight: 1.2, padding: '0 2px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                width: '100%', opacity: 0.7,
              }}>
                {titleSelfConfigure && titleFilled
                  ? (field.source.signatureField?.printedText ?? '')
                  : field.label}
              </span>
            )}

            {/* Signed signature/initials: bottom-anchored, overlays the embedded
                image at the same bbox so the user sees a single signature. */}
            {signed && signaturePreview && (field.type === 'signature' || field.type === 'initials') && (
              <img
                src={signaturePreview}
                alt={field.type === 'initials' ? 'Initials' : 'Signature'}
                style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain', opacity: 1, filter: 'contrast(1.2)' }}
              />
            )}

            {/* Signed markers keep a small check, while text/date/name values
                render in bold black above so applied content stays legible even
                when the PDF's embedded text is faint under the marker wash. */}
            {signed && (
              (field.type === 'date' && !recipientStoredText)
              || (field.type === 'text' && (!recipientStoredText || suppressSignedStoredTextOverlay))
              || (!signaturePreview && (field.type === 'signature' || field.type === 'initials'))
            ) && (
              <div style={{
                position: 'absolute', top: 2, right: 2,
                width: 12, height: 12, borderRadius: 6,
                backgroundColor: PDF_VIEWER.fieldSigned,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 0 1px rgba(255,255,255,0.6)',
              }}>
                <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
                  <path d="M4 8l3 3 5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
